import API_BASE_URL from '../config/api';

const BASE = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

async function managerRequest(endpoint, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  try {
    const { authClient } = await import('../lib/authClient');
    const token = await authClient.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (_) { /* no token */ }

  const response = await fetch(`${BASE}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export const ManagerApi = {
  getConfig() {
    return managerRequest('/manager/config');
  },

  createProduct({ planName, price, interval, description, category, logoUrl }) {
    return managerRequest('/manager/pricing/create', {
      method: 'POST',
      body: { planName, price, interval, description, category, logoUrl },
    });
  },

  updateProduct(productId, { planName, price, interval, description, category, logoUrl }) {
    return managerRequest(`/manager/pricing/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      body: { planName, price, interval, description, category, logoUrl },
    });
  },

  deleteProduct(productId) {
    return managerRequest(`/manager/pricing/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
    });
  },

  createDiscount({ couponName, percentageOff, promoCodeText, productIds, maxRedemptions }) {
    return managerRequest('/manager/discounts/create', {
      method: 'POST',
      body: {
        couponName,
        percentageOff,
        promoCodeText,
        productIds,
        maxRedemptions: maxRedemptions === '' || maxRedemptions == null ? null : maxRedemptions,
      },
    });
  },

  updateDiscount(promoId, { couponName, maxRedemptions }) {
    return managerRequest(`/manager/discounts/${encodeURIComponent(promoId)}`, {
      method: 'PUT',
      body: {
        couponName,
        maxRedemptions: maxRedemptions === '' || maxRedemptions == null ? null : maxRedemptions,
      },
    });
  },

  deactivateDiscount(promoId) {
    return managerRequest(`/manager/discounts/${encodeURIComponent(promoId)}/deactivate`, {
      method: 'POST',
      body: {},
    });
  },

  deleteDiscount(promoId) {
    return managerRequest(`/manager/discounts/${encodeURIComponent(promoId)}`, {
      method: 'DELETE',
    });
  },

  createBypassCode({ code, expiresAt }) {
    return managerRequest('/manager/bypass-codes/create', {
      method: 'POST',
      body: { code, expiresAt: expiresAt || null },
    });
  },

  deactivateBypassCode(bypassId) {
    return managerRequest(`/manager/bypass-codes/${encodeURIComponent(bypassId)}/deactivate`, {
      method: 'POST',
      body: {},
    });
  },

  deleteBypassCode(bypassId) {
    return managerRequest(`/manager/bypass-codes/${encodeURIComponent(bypassId)}`, {
      method: 'DELETE',
    });
  },

  saveOnboardingSteps(customSteps) {
    return managerRequest('/manager/onboarding/steps', {
      method: 'PUT',
      body: { customSteps },
    });
  },

  uploadAsset({ imageBase64, folder = 'general' }) {
    return managerRequest('/manager/upload-asset', {
      method: 'POST',
      body: { imageBase64, folder },
    });
  },
};
